import { useMutation } from '@tanstack/react-query';
import { IdentificationType } from '@prisma/client';
import * as yup from 'yup';
import { Buffer } from 'buffer';
import useAwaitedForm from '../useAwaitedForm';
import { getFileSchema } from '../../types/global';
import { getIDUploadTitle } from '../../helpers/waivers';
import ImagePDF from '../../components/waivers/ImagePDF';
import { mergePDFs, renderPDFSafely } from '../../utils/pdf';
import { compressImage, getStorageUrl } from '../../helpers/global';
import {
  addIDToWaiverRequest,
  fetchWaiverRequest,
} from '../../services/waivers';
import { uploadFiles } from '../../services/global';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import { AddIDToWaiverRequestResponse } from '../../types/waivers';
import useLoadingModal from '../global/useLoadingModal';

const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const validationSchema = yup.object({
  idType: yup
    .mixed<IdentificationType>()
    .oneOf(Object.values(IdentificationType))
    .required(),
  file: getFileSchema(ACCEPTED_FILE_TYPES),
});

interface UseAddIDToWaiverRequestProps {
  onError?: (error: Error) => void;
  onSuccess?: (result: AddIDToWaiverRequestResponse) => void;
}

function useAddIDToWaiverRequest({
  onError,
  onSuccess,
}: UseAddIDToWaiverRequestProps) {
  const asyncForm = useAwaitedForm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const { user } = useUser<LoadedUser>();

  return useMutation({
    mutationFn: async (waiverRequestId: string) => {
      if (!waiverRequestId)
        throw new Error('Waiver request has not been completed yet');

      const formResponse = await asyncForm({
        form: {
          title: 'ID Upload',
          initialValues: {
            idType: 'ID' as IdentificationType,
            file: null as File | null,
          },
          inputRows: [
            [
              {
                inputType: 'select',
                label: 'ID Type',
                name: 'idType',
                options: Object.values(IdentificationType).map(type => ({
                  label: getIDUploadTitle(type),
                  value: type,
                })),
                required: true,
              },
            ],
            [
              {
                inputType: 'file',
                label: 'ID File',
                name: 'file',
                accept: ACCEPTED_FILE_TYPES,
                required: true,
              },
            ],
          ],
          validationSchema,
          submitButtonText: 'Upload',
        },
      });
      if (!formResponse?.idType || !formResponse?.file) return null;

      const compressedFile = await compressImage(formResponse.file, {
        quality: 0.85,
        maxWidth: 600,
        maxHeight: 600,
      });

      openLoadingModal({
        header: 'Uploading ID...',
        message: 'Please wait while we upload the ID to the waiver',
      });

      const { waiverRequest, waiver } = await fetchWaiverRequest({
        waiverRequestId,
      });
      if (!waiverRequest.signedFileLink)
        throw new Error('Waiver request has not been completed yet');

      const originalWaiverPdfUrl = await getStorageUrl(
        waiverRequest.signedFileLink,
      );
      if (!originalWaiverPdfUrl)
        throw new Error('Failed to fetch original waiver PDF');

      const originalWaiverPdfBuffer = await fetch(originalWaiverPdfUrl).then(
        res => res.arrayBuffer(),
      );
      if (!originalWaiverPdfBuffer)
        throw new Error('Failed to fetch original waiver PDF');

      const idBuffer = Buffer.from(await compressedFile.arrayBuffer());

      const pdfBuffer = await renderPDFSafely(
        () => (
          <ImagePDF
            key={Date.now()}
            user={user}
            idType={formResponse.idType}
            buffer={idBuffer}
          />
        ),
        { waiverRequestId },
      );

      const mergedPdfBuffer = await mergePDFs([
        originalWaiverPdfBuffer,
        pdfBuffer,
      ]);
      const pdfLocalUrl = URL.createObjectURL(
        new Blob([mergedPdfBuffer as BlobPart], { type: 'application/pdf' }),
      );

      const tempFileName = `${waiver.business.businessId}-${waiverRequest.appointmentId}`;
      const basePath = `tempImages/${tempFileName}`;

      const uploads = [
        {
          id: 'waiver',
          path: `${basePath}-signed-waiver-updated`,
          url: pdfLocalUrl,
        },
        {
          id: 'id',
          path: `${basePath}-${formResponse.idType}-upload`,
          url: URL.createObjectURL(compressedFile),
        },
      ];

      const [pdfUrl, idUrl] = await uploadFiles(uploads, {
        onSuccess: img => {
          const isObjectUrl = new URL(img.url).protocol === 'blob:';
          if (isObjectUrl) URL.revokeObjectURL(img.url);
        },
      });
      if (!pdfUrl || !idUrl) throw new Error('Failed to upload files');

      const idMediaType = formResponse.file.type;

      const result = await addIDToWaiverRequest({
        waiverRequestId,
        idType: formResponse.idType,
        pdfUrl,
        idUrl,
        idMediaType,
      });

      return result;
    },
    onSuccess: result => {
      if (!result) return;
      onSuccess?.(result);
    },
    onError,
    onSettled: () => {
      closeLoadingModal();
    },
  });
}

export default useAddIDToWaiverRequest;
